import Model from "common/framework/Model";
import PromptInputType from "common/enums/PromptInputType";
import ValidatorType from "common/enums/validators/ValidatorType";
import ValidState from "common/enums/validators/ValidState";
import ElementNodeType from "common/enums/ElementNodeType";
import ObjectUtil from "common/helper/ObjectUtil";
var prompt;
prompt = Model.define({
    id: "id",
    fields: {
        /**
         * label (String) - text to use as the prompt input's label
         */
        label: {},

        /**
         * required (Boolean)
         */
        required: {
            type: "boolean",
            defaultValue: false
        },

        /** Macro that sets required conditionally. **/
        requiredMacro: {},

        /**
         * promptType (String) - one of common/enums/PromptInputType
         *
         */
        inputType: {
            editable: false,
            defaultValue: "TEXT_FIELD"
        },

        /**
         * The original type of the SimpleFormPrompt
         */
        bpmType: {
            editable: false,
            defaultValue: ""
        },

        /**
         * dataType (String - one of ['STRING', 'INTEGER', 'DECIMAL',
         * 'DATE', 'BOOLEAN']
         */
        dataType: {
            // this is editable in form designer, it should not be set anywhere else
            editable: true,
            defaultValue: "STRING"
        },

        /**
         * strDefaultValue (String) - regardless of data type, this is
         * always a string that will be displayed in the input field.
         *
         * This is the default value only if a datasource is not executed for this prompt.
         * If that happens, the defaultValueType and staticDefaultValue need to be used.
         */
        strDefaultValue: {
            // this is editable in form designer, it should not be set anywhere else
            editable: true
        },

        /**
         * defaultValueType (String) - one of PromptDefaultValueType
         * This describes how the default value should be found when data is changed in this prompt.
         */
        defaultValueType: {
            type: "string",
            defaultValue: "NONE"
        },

        /**
         * This is the default value that should be used when defaultValueType = SPECIFIC
         */
        staticDefaultValue: {
            type: "string",
            defaultValue: null
        },

        /**
         * data (Array) - array of data to populate a list; relevant
         * for 'LIST' prompt types.
         */
        data: {
            editable: true
        },

        /**
         * listEditable (Boolean) - relevant only for 'LIST'
         * prompt types; determines whether user can enter a value not in the list's data.
         */
        listEditable: {
            type: "boolean",
            // this is editable in form designer, it should not be set anywhere else
            editable: true,
            defaultValue: false,
            from: "editable"
        },

        /** Property for whether the prompt is enabled or disabled in the UI. **/
        disabled: {},

        /** Macro that sets disabled conditionally. (when true, prompt is DISABLED.) **/
        disabledMacro: {},

        /**
         * maxLength (Integer) - the maximum length allowed to be entered
         * into the input field; relevant for 'TEXT_FIELD' prompt types with dataType 'STRING'.
         */
        maxLength: {
            type: "number",
            editable: false,
            defaultValue: -1
        },

        /**
         * The minimum required number of characters to be entered.  Relevant for TEXT_FIELD prompts with STRING data.
         */
        minLength: {
            type: "number",
            editable: false,
            defaultValue: -1
        },

        /**
         * hidden (Boolean) - will be hidden from the user if true.  No
         * label will be created, and the input will be of type="hidden".
         */
        hidden: {
            type: "boolean",
            defaultValue: false
        },

        /** Macro that sets hidden conditionally. **/
        hiddenMacro: {},

        /**
         * allowMultipleValues (Boolean) - will allow multiple values to be
         * entered by the user.  Can be via a multi-select list, or multiple
         * text entries, depending on other options that are set.
         */
        allowMultipleValues: {
            type: "boolean",
            defaultValue: false
        },

        /**
         * placeholderValue (String) - the text to be displayed as a placeHolder
         * when the input is empty.
         */
        placeholderValue: {
            editable: false
        },

        /**
         * id (String) - id of the prompt entity
         */
        id: {
            editable: false
        },

        /**
         * name (String) - prompt's reference name
         */
        name: {
            editable: false
        },

        /**
         * ordinal (Integer) - zero-based position of the prompt within an array of prompts
         */
        ordinal: {
            type: "number",
            editable: false,
            defaultValue: -1
        },

        /**
         * An array of prompt reference names this prompt is responsible for refreshing,
         * when this prompt's value changes.
         *
         * We moved to responsibilityMapping supporting a {name:[name],field:'VALUE'||'REQUIRED'||'ENABLED'||'HIDDEN'} structure,
         * but will still support a simple string array of field names as well.
         */
        responsibilityMapping: {
            editable: false
        },

        /**
         * If fed by a datasource, that datasource's id.  Needed if this prompt needs to
         * re-execute when other prompts responsible for its value change.
         */
        datasourceId: {
            editable: false
        },

        /**
         * If fed by a macro, that full unprocessed macro.  Needed if this prompt needs to
         * re-execute when other prompts responsible for its value change.
         */
        macroString: {
            editable: false
        },

        /**
         * If fed by a datasource or macro, that datasource or macro's arguments.  Needed if this
         * prompt needs to re-execute when other prompts responsible for its value change.
         */
        parameterPromptDatasourceArguments: {
            editable: false
        },

        /**
         * type (String) - context specific.
         * In Dashboard Prompts - either DASHBOARD_PROMPT, MACRO, ... // TODO: Fill in
         */
        type: {
            editable: false
        },

        /**
         * attributeType (String) - search specific
         */
        attributeType: {
            editable: false
        },

        /**
         * The "ON" label for a Switch prompt
         */
        labelTrue: {
            editable: false
        },

        /**
         * The "OFF" label for a Switch prompt
         */
        labelFalse: {
            editable: false
        },

        /**
         * The endpoint to use for an autocomplete control.  Needs to be a relative path to a REST enabled
         * endpoint on the DocFinity server.  The relative path should not start with a /, and only needs
         * to start with the web service endpoint - you do not need to include /webservices/rest/.
         *
         * Example: to call MetadataWebServices, you would set autoCompleteEndpoint = "metadata".
         *
         * This endpoint only supports GET methods, and expects the target server method to support the standard
         * filtering, paging, and sorting controls.
         */
        autoCompleteEndpoint: {
            editable: false
        },

        /**
         * The model field to use for the text display in the auto complete control.
         */
        autoCompleteTextField: {
            editable: false
        },

        /**
         * The minimum number of characters to type in the auto complete before it filters.
         */
        autoCompleteMinLength: {
            editable: false,
            defaultValue: 1
        },

        /**
         * Optional; if set, along with y, width, and height, completely changes the layout method used.
         *
         * x is responsible for the absolutely-positioned left / right position of the form control in its container.
         */
        x: {
            type: "number"
        },

        /**
         * Optional; if set, along with x, width, and height, completely changes the layout method used.
         *
         * y is responsible for the absolutely-positioned top / bottom position of the form control in its container.
         */
        y: {
            type: "number"
        },

        /**
         * Optional; if set, along with y, x, and height, completely changes the layout method used.
         *
         * Sets an absolute width on the form control, and changes the way readonly mode works for certain control types.
         */
        width: {
            type: "number"
        },

        /**
         * Optional; if set, along with y, width, and x, completely changes the layout method used.
         *
         * Sets an absolute height on the form control, and changes the way readonly mode works for certain control types.
         */
        height: {
            type: "number"
        },

        /**
         * Used only for IMAGE types. The file name of the image, to be used to construct a servlet call to retrieve the image.
         */
        imageName: {},

        characterCase: {},

        blindEntry: {
            type: "boolean",
            defaultValue: false
        },

        precision: {
            type: "number"
        },

        isCurrency: {
            type: "boolean",
            defaultValue: false
        },

        tabIndex: {
            type: "number"
        },

        row: {
            type: "number"
        },

        rowAlign: {
            type: "string"
        },

        rowVAlign: {
            type: "string"
        },

        growType: {
            type: "string"
        },

        renderedWidth: {
            type: "number"
        },

        /**
         * Whether the user is allowed to override error values.  Only applies to DocFinity validators.
         * You cannot override things like required fields.
         */
        allowOverrideErrors: {
            type: "boolean",
            defaultValue: false
        },

        /**
         * Whether this input is invalid, but the user has opted to override the error condition.
         */
        errorsOverridden: {
            type: "boolean",
            defaultValue: false,
            transient: true
        },

        /**
         * Whether the prompt is always readonly.  Used primarily in indexing.
         */
        readonly: {
            type: "boolean",
            defaultValue: false
        },

        originallyReadOnly: {
            type: "boolean",
            defaultValue: false
        },

        datasourceEditable: {
            type: "boolean",
            defaultValue: false
        },

        // Whether to prevent the enter key in text areas
        preventEnterKey: {
            type: "boolean",
            defaultValue: false
        },

        /**
         * Used to store error/warning/valid state of the input.
         */
        validState: {
            type: "string",
            transient: true
        },

        /**
         * Used to store a validation message to show along with the input.  This is an array of strings.
         */
        validMessage: {
            transient: true
        },

        /**
         * Indicator that server validation failed.  When clientOnly validation runs, this is needed to
         * prevent clientOnly validation from clearing out needed messages.
         */
        serverInvalid: {
            type: "boolean",
            transient: true
        },

        /**
         * Used to store the raw value from the input control, separate from the actual value.  No parsing takes place,
         * for example, a date picker's text might be 4/ which is an invalid date.  It's rawValue will reflect that text,
         * so validation can run on it, but the actual value will be null/undefined.
         */
        rawValue: {
            transient: true
        },

        /**
         * Used to store the blind entry value, separate from the actual value.
         */
        blindValue: {
            transient: true
        },

        textStyle: {
            type: "string"
        },

        fontFamily: {
            parse: function(data) {
                if (data) {
                    if (data.toLowerCase().indexOf("verdana") > -1) {
                        return "verdana";
                    } else if (data.toLowerCase().indexOf("times") > -1) {
                        return "times";
                    } else {
                        return "arial";
                    }
                }
            }
        },

        // whether this prompt currently has focus
        focus: {
            type: "boolean",
            transient: true
        },

        datasourceExecuted: {
            type: "boolean",
            defaultValue: false
        },

        datasourceHasOptionalVars: {
            type: "boolean",
            defaultValue: false
        },

        // used for mask formatter validation
        maskTokens: {
            transient: true
        },

        // used for multi-string metadata so we know which value failed validation
        errorIndices: {
            transient: true,
            type: "int"
        },

        // server-provided flag indicating if a prompt contains a value that is different from the stored value
        dirtyIndexData: {
            type: "boolean",
            defaultValue: false
        },


        /**
         * If this is a button, should it skip over required validation on submit.
         */
        bypassRequiredValidation: {
            type: "boolean",
            defaultValue: false
        },
    },

    init: function(options) {
        Model.fn.init.apply(this, arguments);

        if (options) {
            this._init(options);
        }

        var me = this;
        // handle character case transformations.  The input widgets use text-transform to control the display,
        // but don't actually change the value of the text.  We need to handle transforming value and blindValue.
        this.bind("change", function(e) {
            if ((e.field === "value" || e.field == "blindValue") && (me.get("characterCase") === "UPPER" || me.get("characterCase") === "LOWER")) {

                var val = me.get(e.field);
                if (val && typeof val === "string") {
                    me[e.field] = me._transformCase(val);
                } else if (ObjectUtil.isArray(val)) {
                    for (var i = 0; i < val.length; ++i) {
                        val[i] = me._transformCase(val[i]);
                    }
                }
            } else if (e.field == "validState") {
                if (this.get("validState") == ValidState.VALID) {
                    this.errorIndices = undefined;
                }
            }
        });
    },

    _transformCase: function(val) {
        if (this.get("characterCase") === "UPPER") {
            val = val.toUpperCase();
        } else {
            val = val.toLowerCase();
        }
        return val;
    },

    accept: function(obj) {
        Model.fn.accept.apply(this, arguments);
        if (obj) {
            this._init(obj);
        }
    },

    _init: function(options) {
        this.originallyReadOnly = options.readonly;
        options.inputType === PromptInputType.IMAGE && this.setImageSource(options);
        // set a default value on accept, for rare edge cases where it could be missing
        if (!options.inputType && !this.inputType) {
            this.inputType = PromptInputType.TEXT_FIELD;
        }
        // set default text style on readonly text
        if (this.isText() && !this.textStyle) {
            this.textStyle = ElementNodeType.refName(ElementNodeType.PARAGRAPH);
        }
    },

    /**
     * Sets the image name to be the full servlet location for image source
     * @param obj
     */
    setImageSource: function(obj) {
        if (obj.imageId) {
            this.imageId = obj.imageId;
            this.imageName = context.path + "/servlet/form/image/?imageId=" + obj.imageId;
        } else if (obj.imageName && obj.formDesignId) {
            this.formDesignId = obj.formDesignId;
            this.imageName = context.path + "/servlet/form/image/?imageName=" + obj.imageName +
                "&formId=" + obj.formDesignId;
        }
    },

    /**
     * Determine if the given value is acceptable for this prompt type.
     * @param value
     */
    canBeValue: function(value) {
        if (this.inputType == PromptInputType.LIST && !this.listEditable) {
            if (!this.data) {
                return false;
            } else {
                var values;
                if (this.allowMultipleValues && value && value.length) {
                    values = value;
                } else {
                    values = [value];
                }
                var me = this;
                return values.every(function(value) {
                    return me.data.some(function(canidate) {
                        return canidate.value == value;
                    });
                });

            }
        } else {
            return true;
        }
    },

    /**
     * Determine if the prompt is currently in a valid state.
     */
    isValid: function() {
        if (this.required) {
            return !!this.value && this.canBeValue(this.value);
        } else {
            return !this.value || (!!this.value && this.canBeValue(this.value));
        }
    },

    getValue() {
        if(this.inputType === PromptInputType.LIST_VIEW
            && !this.allowMultipleValues
            && ObjectUtil.isArray(this.value)) {
            //The list view control returns an array, but this isn't correct in single selection mode.
            if (this.value.length === 1) {
                return this.value[0];
            } else {
                return null;
            }
        }
        return this.value;
    },

    hasMaskValidator: function() {
        var ret = false;
        if (this.validators) {
            ret = this.validators.some(this._isMaskValidator);
        }
        return ret;
    },

    _isMaskValidator: function(validator) {
        return validator.validatorType === ValidatorType.MASK;
    },

    getMaskFormat: function() {
        var ret = "";
        if (this.validators) {
            var validator = $.grep(this.validators, this._isMaskValidator);
            if (validator.length > 0) {
                // you can only configure one mask validator per form item.  only worrying about the first one.
                validator = validator[0];
                if (validator.properties) {
                    var mask = $.grep(validator.properties, function(prop) {
                        return prop.name === "maskFormat";
                    });

                    if (mask.length > 0) {
                        ret = mask[0].value;
                    }
                }
            }
        }
        return ret;
    },

    /**
     * Whether the prompt has a label or not
     * @returns {boolean}
     */
    hasLabel: function() {
        return this.inputType === PromptInputType.SWITCH || this.inputType === PromptInputType.DATE_FIELD || this.inputType === PromptInputType.LIST || this.inputType === PromptInputType.LIST_VIEW || this.inputType === PromptInputType.TABLE || this.inputType === PromptInputType.TEXT_AREA || this.inputType === PromptInputType.TEXT_FIELD
    },

    /**
     * Whether the prompt is a line or not
     * @returns {boolean}
     */
    isLine: function() {
        return this._isHLine() || this._isVLine();
    },

    _isHLine: function() {
        return this.inputType === PromptInputType.H_LINE;
    },

    _isVLine: function() {
        return this.inputType === PromptInputType.V_LINE;
    },

    /**
     * Whether the prompt is an image or not
     * @returns {boolean}
     */
    isImage: function() {
        return this.inputType === PromptInputType.IMAGE;
    },

    /**
     * Whether the prompt is read-only text
     * @returns {boolean}
     */
    isText: function() {
        return this.inputType === PromptInputType.READ_ONLY;
    },

    /**
     * Whether the controls width can be resized
     * @returns {boolean}
     */
    canResizeWidth: function() {
        return this.inputType === PromptInputType.LIST || this.inputType === PromptInputType.LIST_VIEW || this.inputType === PromptInputType.TEXT_FIELD || this.inputType === PromptInputType.TEXT_AREA || this.inputType === PromptInputType.BUTTON || this.inputType === PromptInputType.FILE_UPLOAD || this.inputType === PromptInputType.LINK || this.inputType === PromptInputType.H_LINE || this.inputType === PromptInputType.IMAGE || this.inputType === PromptInputType.READ_ONLY || this.inputType === PromptInputType.TABLE;
    },

    /**
     * Whether the controls height can be resized
     * @returns {boolean}
     */
    canResizeHeight: function() {
        return this.inputType === PromptInputType.LIST_VIEW || this.inputType === PromptInputType.TEXT_AREA || this.inputType === PromptInputType.BUTTON || this.inputType === PromptInputType.FILE_UPLOAD || this.inputType === PromptInputType.V_LINE || this.inputType === PromptInputType.IMAGE || this.inputType === PromptInputType.TABLE;
    }
});
export default prompt;
